import { Component, EventListener } from '@gondel/core';
import { GondelJqueryComponent } from '@gondel/plugin-jquery';
import { getCurrentViewport, VIEWPORT_ENTERED } from '@gondel/plugin-media-queries';
import { WINDOW_RESIZED } from '@swisslife/shared-ui/src/utils/gondel-resize/js/gondel-resize';

const enum Selectors {
	CollapseStepperTrigger = '.js-m-collapse-stepper__item-trigger',
	CollapseStepperContentWrapper = '.js-m-collapse-stepper__content-wrapper',
	CollapseStepperContent = '.js-m-collapse-stepper__content',
	CollapseStepperShowAllButton = '.js-m-collapse-stepper__button-show-all',
	CollapseStepperShowMoreButton = '.js-m-collapse-stepper__button-show-more',
	CollapseStepperShowLessButton = '.js-m-collapse-stepper__button-show-less',
	CollapseStepperHideAllButton = '.js-m-collapse-stepper__button-hide-all',
}

const enum States {
	CollapseStepperActive = 'state-m-collapse-stepper--active',
	CollapseStepperTriggerHidden = 'state-m-collapse-stepper--trigger-hidden',
	CollapseStepperDisableButton = 'state-m-collapse-stepper--disable-button',
}

@Component('CollapseStepper')
export class CollapseStepper extends GondelJqueryComponent {
	private $showAllButton: any;
	private $showMoreButton: any;
	private $showLessButton: any;
	private $hideAllButton: any;
	private $collapseContentWrapper: any;
	private $collapseContent: any;
	private $triggerElements: any;
	private uxBehaviourType: string;
	private itemsPerRows: any = undefined;
	private currentHeight: number = 0;
	private refreshHeightTimeout: any;

	public start() {
		this.$collapseContentWrapper = this.$ctx.find(Selectors.CollapseStepperContentWrapper);
		this.$showAllButton = this.$ctx.find(Selectors.CollapseStepperShowAllButton);
		this.$hideAllButton = this.$ctx.find(Selectors.CollapseStepperHideAllButton);
		this.$showMoreButton = this.$ctx.find(Selectors.CollapseStepperShowMoreButton);
		this.$showLessButton = this.$ctx.find(Selectors.CollapseStepperShowLessButton);
		if (this.$collapseContentWrapper) {
			this.setItemsPerRows();
			this.$collapseContent = this.$collapseContentWrapper.find(Selectors.CollapseStepperContent);
			this.$triggerElements = this.$collapseContentWrapper.find(Selectors.CollapseStepperTrigger);
			this.currentHeight = 0;
			this.hideAllTriggerElements(false);
			if (!this.$triggerElements || this.$triggerElements.length === 0) {
				this.disableButton(this.$showMoreButton);
				this.disableButton(this.$showLessButton);
			}
			this.$ctx.addClass(States.CollapseStepperActive);
		}
		this.updateUxBehaviourType(getCurrentViewport());
	}

	@EventListener('click', Selectors.CollapseStepperShowAllButton)
	public _onShowAll(ev: any) {
		ev.preventDefault();
		if (this.isButtonEnable(this.$showAllButton)) {
			this.showAllTriggerElements();
		}
	}

	@EventListener('click', Selectors.CollapseStepperShowMoreButton)
	public _onShowMore(ev: any) {
		ev.preventDefault();
		if (this.isButtonEnable(this.$showMoreButton)) {
			this.showMoreTriggerElements();
		}
	}

	@EventListener('click', Selectors.CollapseStepperShowLessButton)
	public _onShowLess(ev: any) {
		ev.preventDefault();
		if (this.isButtonEnable(this.$showLessButton)) {
			this.showLessTriggerElements();
		}
	}

	@EventListener('click', Selectors.CollapseStepperHideAllButton)
	public _onHideAll(ev: any) {
		ev.preventDefault();
		if (this.isButtonEnable(this.$hideAllButton)) {
			this.hideAllTriggerElements();
		}
	}

	@EventListener(VIEWPORT_ENTERED)
	public _handleViewportChangeEvent(ev: any) {
		this.updateUxBehaviourType(ev.viewport);
	}

	@EventListener(WINDOW_RESIZED)
	public _handleWindowResizeEvent() {
		this.updateUxBehaviourType(getCurrentViewport());
	}

	private updateUxBehaviourType(viewport: string) {
		this.uxBehaviourType = viewport;
	}

	private setItemsPerRows() {
		this.itemsPerRows = {};
		const sizes = ['xxsmall', 'xsmall', 'small', 'medium', 'large', 'xlarge', 'xxlarge'];
		sizes.forEach((size) => {
			this.itemsPerRows[size] = this.$ctx.data(`items-per-row-${size}`) || 0;
		});
		const nextSizes = sizes.concat([]);
		const findNextShowing = (defaultValue: number = 0) => {
			const reversedSizes = nextSizes.concat([]).reverse();
			for (const index in reversedSizes) {
				if (this.itemsPerRows[reversedSizes[index]] > 0) {
					return this.itemsPerRows[reversedSizes[index]];
				}
			}
			return defaultValue;
		};
		let nextShowing = findNextShowing();
		while (nextSizes.length > 0) {
			const currentSize = nextSizes.pop();
			nextShowing = findNextShowing(nextShowing);
			if (currentSize && this.itemsPerRows[currentSize] === 0 && nextShowing > 0) {
				this.itemsPerRows[currentSize] = nextShowing;
			}
		}
	}

	private refreshHeight(height?: number) {
		if (this.$collapseContentWrapper) {
			if (height === undefined) {
				this.$collapseContentWrapper.css('height', this.currentHeight);
				this.$collapseContentWrapper.css('overflow', 'scroll');
				const nextHeight = this.$collapseContent.height();
				this.$collapseContentWrapper.css('overflow', '');
				this.$collapseContentWrapper.css('height', nextHeight);
				clearTimeout(this.refreshHeightTimeout);
				this.refreshHeightTimeout = setTimeout(() => {
					this.refreshHeightTimeout = null;
					this.$collapseContentWrapper.css('height', '');
				}, 400); // animation duration time
			} else {
				this.$collapseContentWrapper.css('height', height);
			}
		}
	}

	private getVisibleElementsIndex(): number {
		for (let index = this.$triggerElements.length - 1; index >= 0; index--) {
			const $element = $(this.$triggerElements.get(index));
			if ($element && !$element.hasClass(States.CollapseStepperTriggerHidden)) {
				return index;
			}
		}
		return -1;
	}

	private showAllTriggerElements(calcCurrentHeight: boolean = true) {
		this.currentHeight = calcCurrentHeight
			? this.$collapseContent && this.$collapseContent.height()
			: this.currentHeight;
		if (this.$triggerElements && this.$triggerElements.length) {
			this.$triggerElements.each((index, element) => {
				const $element = $(element);
				if ($element) {
					$element.removeClass(States.CollapseStepperTriggerHidden);
				}
			});
		}
		this.refreshHeight();
		this.disableButton(this.$showAllButton);
		this.enableButton(this.$hideAllButton);
		if (this.$triggerElements && this.$triggerElements.length) {
			this.disableButton(this.$showMoreButton);
			this.enableButton(this.$showLessButton);
		}
	}

	private showMoreTriggerElements() {
		this.currentHeight = this.$collapseContent && this.$collapseContent.height();
		const show = (this.itemsPerRows && this.itemsPerRows[this.uxBehaviourType]) || 0;
		if (show) {
			if (this.$triggerElements && this.$triggerElements.length) {
				const index = this.getVisibleElementsIndex() + 1;
				for (let i = 0; i < show; i++) {
					const $element = $(this.$triggerElements.get(index + i));
					if ($element && $element.hasClass(States.CollapseStepperTriggerHidden)) {
						$element.removeClass(States.CollapseStepperTriggerHidden);
					}
				}
				this.enableButton(this.$showLessButton);
				this.enableButton(this.$hideAllButton);
				if (index + show >= this.$triggerElements.length) {
					this.showAllTriggerElements(false);
				}
			}
			this.refreshHeight();
		} else {
			this.showAllTriggerElements(false);
		}
	}

	private showLessTriggerElements() {
		this.currentHeight = this.$collapseContent && this.$collapseContent.height();
		const show = (this.itemsPerRows && this.itemsPerRows[this.uxBehaviourType]) || 0;
		if (show) {
			if (this.$triggerElements && this.$triggerElements.length) {
				const index = this.getVisibleElementsIndex() + 1;
				for (let i = index % show > 0 ? index % show : show; i >= 0; i--) {
					const $element = $(this.$triggerElements.get(index - i));
					if ($element && !$element.hasClass(States.CollapseStepperTriggerHidden)) {
						$element.addClass(States.CollapseStepperTriggerHidden);
					}
				}
				this.enableButton(this.$showAllButton);
				this.enableButton(this.$showMoreButton);
				if (index - show <= 0) {
					this.hideAllTriggerElements(false);
				}
			}
			this.refreshHeight();
		} else {
			this.hideAllTriggerElements(false);
		}
	}

	private hideAllTriggerElements(calcCurrentHeight: boolean = true) {
		this.currentHeight = calcCurrentHeight
			? this.$collapseContent && this.$collapseContent.height()
			: this.currentHeight;
		if (this.$triggerElements && this.$triggerElements.length) {
			this.$triggerElements.each((index, element) => {
				const $element = $(element);
				if ($element) {
					$element.addClass(States.CollapseStepperTriggerHidden);
				}
			});
		}
		this.refreshHeight();
		this.enableButton(this.$showAllButton);
		this.disableButton(this.$hideAllButton);
		if (this.$triggerElements && this.$triggerElements.length) {
			this.enableButton(this.$showMoreButton);
			this.disableButton(this.$showLessButton);
		}
	}

	private isButtonEnable($button: any) {
		return $button && !$button.hasClass(States.CollapseStepperDisableButton);
	}

	private enableButton($button: any) {
		if ($button) {
			$button.removeClass(States.CollapseStepperDisableButton);
			$button.removeAttr('disable');
		}
	}

	private disableButton($button: any) {
		if ($button) {
			$button.addClass(States.CollapseStepperDisableButton);
			$button.attr('disable', true);
		}
	}
}
